import React, { useState, useEffect } from 'react';
import { FiCheck, FiX, FiClock, FiUser, FiCreditCard, FiFilter, FiTrendingUp, FiArrowRight, FiCalendar, FiRefreshCw, FiSearch, FiEye, FiEyeOff, FiAlertCircle, FiGlobe, FiStar, FiPackage, FiShoppingCart, FiDollarSign, FiActivity, FiBarChart2, FiPieChart, FiTarget, FiZap, FiShield, FiAward, FiMapPin, FiHome, FiTruck, FiNavigation, FiMail, FiPhone, FiEdit, FiTrash2, FiMoreVertical, FiSettings, FiLock, FiUnlock, FiCheckCircle, FiXCircle, FiChevronDown, FiChevronUp, FiChevronLeft, FiChevronRight, FiPlus, FiMinus, FiDownload, FiUpload } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { socket } from '../../socket/socket';
import { useAuth } from '../../context/AuthContext';

const PlanRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showGenerateBillModal, setShowGenerateBillModal] = useState(false);
  const [billForm, setBillForm] = useState({
    customPrice: '',
    gstNumber: ''
  });

  useEffect(() => {
    fetchRequests();

    // Socket room join is handled globally in Sidebar.js
    if (user?.role === 'superadmin') {
      // Listen for new plan requests
      socket.on('planRequest:new', (payload) => {
        toast.success(payload.message || 'New plan request received');
        fetchRequests(); // Refresh the list
      });

      return () => {
        socket.off('planRequest:new');
      };
    }
  }, [user]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  const fetchRequests = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing plan requests...', { id: 'refresh' });
      }

      console.log('Fetching plan requests...');
      const response = await api.get('/superadmin/plan-requests');
      console.log('Plan requests response:', response.data);

      if (response.data.success) {
        setRequests(response.data.data);

        // Show success message for manual refresh
        if (isManualRefresh) {
          toast.success(`Refreshed! Found ${response.data.data.length} requests`, { id: 'refresh' });
        }
      } else {
        toast.error('Failed to load plan requests data', { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch plan requests error:', error);
      toast.error(`Failed to fetch plan requests: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this plan request?')) return;

    try {
      const response = await api.post(`/superadmin/plan-requests/${requestId}/approve`);
      if (response.data.success) {
        toast.success('Plan request approved successfully');
        fetchRequests();
      }
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectNotes('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    try {
      const response = await api.post(`/superadmin/plan-requests/${selectedRequest._id}/reject`, {
        notes: rejectNotes
      });
      if (response.data.success) {
        toast.success('Plan request rejected');
        setShowRejectModal(false);
        setSelectedRequest(null);
        setRejectNotes('');
        fetchRequests();
      }
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleGenerateBill = async (request) => {
    try {
      setSelectedRequest(request);
      setShowGenerateBillModal(true);
      setBillForm({
        customPrice: '',
        gstNumber: request.vendor?.restaurantDetails?.gstNumber?.toUpperCase() || ''
      })
      console.log(request,"requestrequest")
    } catch (error) {
      toast.error('Failed to open bill form');
    }
  };

  const handleGenerateBillSubmit = async () => {
    try {
      const response = await api.post('/superadmin/subscription-plan/generate-bill', {
        planRequestId: selectedRequest._id,
        vendorId: selectedRequest.vendor?._id,
        planId: selectedRequest.requestedPlan?._id,
        customPrice: billForm.customPrice || selectedRequest.requestedPlan?.price,
        gstNumber: billForm.gstNumber
      });

      if (response.data.success) {
        toast.success('Bill generated successfully!');
        setShowGenerateBillModal(false);
        setBillForm({
          customPrice: '',
          gstNumber: selectedRequest.vendor?.restaurantDetails?.gstNumber?.toUpperCase() || ''
        });
        fetchRequests();
      }
    } catch (error) {
      toast.error('Failed to generate bill');
    }
  };

  const filteredRequests = requests.filter(req => {
    // Status filter
    const statusMatch = filter === 'all' || req.status === filter;

    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const searchMatch = !searchTerm ||
      req.vendor?.personalDetails?.name?.toLowerCase().includes(searchLower) ||
      req.vendor?.personalDetails?.email?.toLowerCase().includes(searchLower) ||
      req.vendor?.restaurantDetails?.restaurantName?.toLowerCase().includes(searchLower) ||
      req.requestedPlan?.name?.toLowerCase().includes(searchLower) ||
      req.currentPlan?.name?.toLowerCase().includes(searchLower);

    return statusMatch && searchMatch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading Plan Requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Enhanced Header */}
      <div className="bg-white border-b border-gray-200">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">

    <div className="flex items-center justify-between flex-wrap gap-4">

      {/* Left Section */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow">
          <FiTrendingUp className="text-white text-lg" />
        </div>

        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Plan Requests
          </h1>
          <p className="text-xs text-gray-500">
            Manage vendor subscription plan change requests
          </p>
        </div>
      </div>


      {/* Right Section */}
      <div className="flex items-center gap-3">

        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
          <FiActivity className="text-blue-600 text-sm" />
          {requests.length} Total
        </div>

        <button
          onClick={() => fetchRequests(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
        >
          <FiRefreshCw className={`text-sm ${refreshing ? "animate-spin" : ""}`} />
          <span className="text-sm">
            {refreshing ? "Refreshing..." : "Refresh"}
          </span>
        </button>

      </div>

    </div>

  </div>
</div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">

          {/* Total */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded-md">
                  <FiActivity className="text-blue-600 text-sm" />
                </div>
                <span className="text-sm text-gray-600 font-medium">Total</span>
              </div>

              <span className="text-lg font-bold text-gray-800">
                {requests.length}
              </span>
            </div>
          </div>


          {/* Pending */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-100 rounded-md">
                  <FiClock className="text-amber-600 text-sm" />
                </div>
                <span className="text-sm text-gray-600 font-medium">Pending</span>
              </div>

              <span className="text-lg font-bold text-amber-600">
                {requests.filter(r => r.status === 'pending').length}
              </span>
            </div>
          </div>


          {/* Approved */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-100 rounded-md">
                  <FiCheck className="text-green-600 text-sm" />
                </div>
                <span className="text-sm text-gray-600 font-medium">Approved</span>
              </div>

              <span className="text-lg font-bold text-green-600">
                {requests.filter(r => r.status === 'approved').length}
              </span>
            </div>
          </div>


          {/* Rejected */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-red-100 rounded-md">
                  <FiX className="text-red-600 text-sm" />
                </div>
                <span className="text-sm text-gray-600 font-medium">Rejected</span>
              </div>

              <span className="text-lg font-bold text-red-600">
                {requests.filter(r => r.status === 'rejected').length}
              </span>
            </div>
          </div>

        </div>

        {/* Enhanced Search Bar with Filter */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center pointer-events-none">
                <FiSearch className="text-gray-400" size={18} />
              </div>
              <input
                type="text"
                placeholder="Search by vendor name, email, restaurant, or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none text-gray-700 placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-gray-100 hover:bg-gray-200 border-none rounded-lg px-3 py-1 text-xs font-semibold text-gray-700 cursor-pointer transition-all duration-200"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="flex items-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-gray-700 transition-colors duration-200"
              >
                <FiFilter size={16} />
                <span>
                  {filter === 'all' ? 'All Status' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                </span>
                <FiChevronDown className={`transition-transform duration-200 ${showFilterDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => { setFilter('all'); setShowFilterDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-200 ${filter === 'all' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                    >
                      All Status
                    </button>
                    <button
                      onClick={() => { setFilter('pending'); setShowFilterDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-200 ${filter === 'pending' ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-700'
                        }`}
                    >
                      Pending
                    </button>
                    <button
                      onClick={() => { setFilter('approved'); setShowFilterDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-200 ${filter === 'approved' ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-700'
                        }`}
                    >
                      Approved
                    </button>
                    <button
                      onClick={() => { setFilter('rejected'); setShowFilterDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-200 ${filter === 'rejected' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700'
                        }`}
                    >
                      Rejected
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Requests Cards Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FiFilter className="text-gray-400" size={20} />
            <h2 className="text-xl font-bold text-gray-900">
              {filter === 'all' ? 'All Requests' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Requests`}
            </h2>
            <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-semibold text-gray-600">
              {filteredRequests.length}
            </span>
          </div>

          {filteredRequests.length > 0 && (
            <div className="text-sm text-gray-500">
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredRequests.length)} of {filteredRequests.length}
            </div>
          )}
        </div>

        {/* Enhanced Empty State */}
        {filteredRequests.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-20 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-8">
              <FiCreditCard className="text-gray-400 text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-3">No requests found</h3>
            <p className="text-gray-500 mb-6">
              {filter === 'all' ? 'No plan requests available' : `No ${filter} requests at the moment`}
            </p>
            <button
              onClick={() => setFilter('all')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              View All Requests
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Table Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FiCreditCard className="text-gray-600" />
                {filter === 'all' ? 'All Requests' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Requests`}
                <span className="px-3 py-1 bg-gray-200 rounded-full text-sm font-semibold text-gray-600">
                  {filteredRequests.length}
                </span>
              </h2>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restaurant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentRequests.map((request, index) => (
                    <tr key={request._id} className={`hover:bg-gray-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                      }`}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          #{request._id.slice(-8)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                            <FiUser className="text-white text-sm" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {request.vendor?.personalDetails?.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.vendor?.personalDetails?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {request.vendor?.restaurantDetails?.restaurantName}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {request.currentPlan ? (
                          <div className="px-3 py-2 bg-gray-100 rounded-lg">
                            <div className="text-sm font-medium text-gray-900">
                              {request.currentPlan.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              ₹{request.currentPlan.price} / {request.currentPlan.duration} days
                            </div>
                          </div>
                        ) : (
                          <div className="px-3 py-2 bg-red-50 rounded-lg text-red-700 text-sm">
                            No active plan
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="text-sm font-medium text-blue-900">
                            {request.requestedPlan?.name}
                          </div>
                          <div className="text-xs text-blue-600">
                            ₹{request.requestedPlan?.price} / {request.requestedPlan?.duration} days
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {new Date(request.requestedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full items-center gap-2 ${request.status === 'pending'
                            ? 'bg-amber-100 text-amber-800'
                            : request.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                          {request.status === 'pending' && <FiClock className="text-xs" />}
                          {request.status === 'approved' && <FiCheck className="text-xs" />}
                          {request.status === 'rejected' && <FiX className="text-xs" />}
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {request.status === 'pending' ? (
                          <div className="flex flex-col gap-2">
                            <button
                              className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors duration-200 w-full"
                              onClick={() => handleApprove(request._id)}
                            >
                              Approve
                            </button>
                            <button
                              className="px-3 py-1 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors duration-200 w-full"
                              onClick={() => openRejectModal(request)}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            {request.status === 'approved' && !request.isBillGenerated ? (
                              <>
                                <div className="text-sm text-gray-500 text-center">
                                  Ready for Billing
                                </div>
                                <button
                                  className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors duration-200 w-full"
                                  onClick={() => handleGenerateBill(request)}
                                >
                                  Generate Bill
                                </button>
                              </>
                            ) : request.status === 'approved' && request.isBillGenerated ? (
                              <div className="text-sm text-green-600 text-center font-medium">
                                <FiCheckCircle className="inline mr-1" />
                                Bill Generated
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 text-center">
                                {request.status === 'rejected' ? 'Rejected' : 'Processed'}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Table Footer with Pagination Info */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredRequests.length)} of {filteredRequests.length} requests
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Pagination */}
        {filteredRequests.length > itemsPerPage && (
          <div className="flex justify-center items-center gap-2 mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
            >
              Previous
            </button>

            <div className="flex gap-1">
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;

                // Show first page, last page, current page, and pages around current
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-200 min-w-[40px] ${currentPage === pageNumber
                          ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return (
                    <span
                      key={pageNumber}
                      className="px-2 py-2 text-gray-400 text-sm"
                    >
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
            >
              Next
            </button>
          </div>
        )}

        {/* Enhanced Reject Modal */}
        {showRejectModal && selectedRequest && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowRejectModal(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Reject Plan Request</h2>
                <p className="text-sm text-gray-600">
                  Provide a reason for rejecting this request (optional)
                </p>
              </div>

              <div className="p-6">
                <div className="px-4 py-3 bg-red-50 rounded-lg border border-red-200 mb-4">
                  <div className="text-sm font-semibold text-red-800 mb-1">
                    {selectedRequest.vendor?.personalDetails?.name}
                  </div>
                  <div className="text-xs text-red-700">
                    Requesting: {selectedRequest.requestedPlan?.name}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason
                  </label>
                  <textarea
                    value={rejectNotes}
                    onChange={(e) => setRejectNotes(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    className="w-full min-h-[100px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-vertical"
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    className="px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-semibold text-sm hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    onClick={() => setShowRejectModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold text-sm flex items-center gap-2 hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    onClick={handleReject}
                  >
                    <FiX size={16} className="group-hover:scale-110 transition-transform" />
                    Reject Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generate Bill Modal */}
        {showGenerateBillModal && selectedRequest && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowGenerateBillModal(false)}
          >
            <div 
              className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Generate Bill</h2>
                <p className="text-sm text-gray-600">
                  Create bill for {selectedRequest.vendor?.personalDetails?.name}
                </p>
              </div>

              <div className="p-6">
                <div className="px-4 py-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                  <div className="text-sm font-semibold text-blue-800 mb-1">
                    {selectedRequest.vendor?.personalDetails?.name}
                  </div>
                  <div className="text-xs text-blue-700">
                    Plan: {selectedRequest.requestedPlan?.name}
                  </div>
                  <div className="text-xs text-blue-700">
                    Original Price: ₹{selectedRequest.requestedPlan?.price}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Price
                  </label>
                  <input
                    type="number"
                    value={billForm.customPrice}
                    onChange={(e) => setBillForm({...billForm, customPrice: e.target.value})}
                    placeholder={selectedRequest.requestedPlan?.price}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to use original plan price: ₹{selectedRequest.requestedPlan?.price}
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Number
                  </label>
                  <input
                    type="text"
                    value={billForm.gstNumber}
                    onChange={(e) => setBillForm({...billForm, gstNumber: e.target.value})}
                    placeholder="Enter GST Number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100 text-gray-500"
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter GST number for billing purposes
                  </p>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    className="px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-semibold text-sm hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    onClick={() => setShowGenerateBillModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold text-sm flex items-center gap-2 hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    onClick={handleGenerateBillSubmit}
                  >
                    <span className="text-base">₹</span>
                    Generate Bill
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanRequests;
